import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Mail, History, Send, RefreshCw, FileText, CheckCircle2, AlertCircle } from 'lucide-react';
import { getEmailLogs } from '@/lib/api';
import { toast } from 'sonner';

const EMAIL_TYPE_CONFIG = {
  invoice: {
    label: 'Invoice',
    icon: FileText,
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  welcome: {
    label: 'Welcome',
    icon: Mail,
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  custom: {
    label: 'Custom',
    icon: Mail,
    badge: 'bg-slate-100 text-slate-700 border-slate-200',
  },
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const OrderEmailsTab = ({ orderId, onSendEmail, customerEmail }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchLogs = useCallback(async (silent = false) => {
    if (!orderId) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await getEmailLogs({ order_id: orderId });
      setLogs(res.data || []);
    } catch {
      if (!silent) toast.error('Failed to load email history');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [orderId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSendAndRefresh = async () => {
    if (onSendEmail) {
      await onSendEmail();
      // Give backend a moment then refresh logs
      setTimeout(() => fetchLogs(true), 1500);
    }
  };

  return (
    <Card className="bg-white border border-slate-200/50 shadow-sm">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="w-5 h-5" />
              Email History
            </CardTitle>
            <CardDescription className="mt-1">
              All email communications sent for this order
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchLogs(true)}
              disabled={refreshing}
              className="h-8 gap-1.5 text-slate-600"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {onSendEmail && (
              <Button
                size="sm"
                onClick={handleSendAndRefresh}
                disabled={!customerEmail}
                className="h-8 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
                title={!customerEmail ? 'No customer email on file' : `Send invoice to ${customerEmail}`}
              >
                <Send className="w-3.5 h-3.5" />
                Send Invoice
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-lg" />
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center px-6">
            <Mail className="w-12 h-12 text-slate-300 mb-3" strokeWidth={1.5} />
            <p className="text-base font-medium text-slate-600 mb-1">No emails sent yet</p>
            <p className="text-sm text-slate-400">
              {customerEmail
                ? `Click "Send Invoice" to email the order invoice to ${customerEmail}`
                : 'Add a customer email address to this order to send emails'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {logs.map((log) => {
              const cfg = EMAIL_TYPE_CONFIG[log.email_type] || EMAIL_TYPE_CONFIG.custom;
              const Icon = cfg.icon;
              return (
                <div key={log.log_id} className="flex items-start gap-4 px-6 py-4 hover:bg-slate-50/60 transition-colors">
                  <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${cfg.badge} border text-[11px] font-medium px-2 py-0`}>
                        {cfg.label}
                      </Badge>
                      {log.status === 'sent' ? (
                        <span className="flex items-center gap-1 text-[11px] text-emerald-700 font-medium">
                          <CheckCircle2 className="w-3 h-3" /> Sent
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[11px] text-red-600 font-medium">
                          <AlertCircle className="w-3 h-3" /> Failed
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-800 mt-1 truncate">
                      {log.subject || '—'}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
                      <span className="text-xs text-slate-500">
                        <span className="text-slate-400">To:</span> {log.to_email}
                      </span>
                      <span className="text-xs text-slate-500">
                        <span className="text-slate-400">From:</span> {log.from_email}
                      </span>
                      {log.sent_by && (
                        <span className="text-xs text-slate-500">
                          <span className="text-slate-400">By:</span> {log.sent_by}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-xs text-slate-400 whitespace-nowrap">{formatDate(log.sent_at)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderEmailsTab;
